import { Link, useNavigate } from "react-router-dom";
import { useI18n } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Languages, LogOut, ShieldCheck, Sparkles } from "lucide-react";
import { ImpersonationBanner } from "./ImpersonationBanner";

export function Header() {
  const { t, lang, toggle } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <>
      <ImpersonationBanner />
      <header
        className="sticky top-0 z-50 backdrop-blur-xl bg-[#FDFBF7]/80 border-b border-black/5"
        data-testid="site-header"
      >
      <div className="mx-auto max-w-7xl px-6 md:px-10 h-16 flex items-center justify-between gap-4">
        <Link
          to="/"
          className="flex items-center gap-2 font-black text-xl tracking-tight"
          data-testid="logo-link"
        >
          <span className="inline-block h-8 w-8 rounded-2xl bg-[#4361EE] grid place-items-center text-white shadow-[0_4px_0_#2A3EB1]">
            <span className="text-lg">✦</span>
          </span>
          <span>{lang === "ar" ? "سباركل" : "Sparkl"}</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm font-semibold text-black/70">
          <a href="/#how" className="hover:text-[#4361EE] transition" data-testid="nav-what-learn">
            {t("nav.whatLearn")}
          </a>
          <a href="/#ages" className="hover:text-[#4361EE] transition" data-testid="nav-ages">
            {t("nav.ages")}
          </a>
          <Link to="/courses" className="hover:text-[#4361EE] transition" data-testid="nav-courses">
            {t("nav.courses")}
          </Link>
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={toggle}
            className="inline-flex items-center gap-1 rounded-full px-3 py-2 text-sm font-bold border-2 border-black/10 hover:border-black/30 transition"
            data-testid="lang-toggle"
            aria-label="Toggle language"
          >
            <Languages className="h-4 w-4" />
            <span>{t("lang.toggle")}</span>
          </button>

          {user ? (
            <>
              {user.role === "super_admin" && (
                <Button
                  onClick={() => navigate("/superadmin")}
                  className="hidden md:inline-flex rounded-full bg-[#F72585] hover:bg-[#F72585]/90 text-white font-bold px-4 shadow-[0_3px_0_#B00060]"
                  data-testid="header-superadmin-btn"
                >
                  <ShieldCheck className="h-4 w-4 me-1" /> {t("superadmin.nav")}
                </Button>
              )}
              {(user.role === "admin" || user.role === "super_admin") && (
                <Button
                  onClick={() => navigate("/admin/courses")}
                  variant="outline"
                  className="hidden md:inline-flex rounded-full font-bold border-2 border-[#4361EE] text-[#4361EE] hover:bg-[#4361EE]/5"
                  data-testid="header-admin-btn"
                >
                  <Sparkles className="h-4 w-4 me-1" /> {t("admin.nav")}
                </Button>
              )}
              <Button
                onClick={() => navigate("/dashboard")}
                className="rounded-full bg-[#1A1A1A] text-white hover:bg-[#1A1A1A]/90 font-bold px-5 shadow-[0_3px_0_#000] hover:translate-y-[1px] hover:shadow-[0_2px_0_#000] transition"
                data-testid="header-dashboard-btn"
              >
                {t("dashboard.welcome")}, {user.name.split(" ")[0]}
              </Button>
              <Button
                variant="ghost"
                onClick={async () => {
                  await logout();
                  navigate("/");
                }}
                className="rounded-full font-bold"
                data-testid="header-logout-btn"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="header-login-link">
                <Button
                  variant="ghost"
                  className="rounded-full font-bold text-black/80 hover:bg-black/5"
                >
                  {t("nav.login")}
                </Button>
              </Link>
              <Link to="/signup" data-testid="header-signup-link">
                <Button className="rounded-full bg-[#4361EE] hover:bg-[#4361EE]/90 text-white font-bold px-5 shadow-[0_3px_0_#2A3EB1] hover:translate-y-[1px] hover:shadow-[0_2px_0_#2A3EB1] transition">
                  {t("nav.signup")}
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
      </header>
    </>
  );
}
